import { Injectable, inject } from '@angular/core';
import { ITenantWorkspacesSnapshot, ITenantWorkspacesSnapshotPlatformExtraInfo, TENANTWORKSPACESSNAPSHOTINFO } from '../../models/pulse365-overview/tenant-workspace-snapshot.model';
import { ApiDataService } from 'src/app/services/http-data/apidata.service';
import { ITenantActivityArchivedTotals, ITenantActivityCreatedTotals, ITenantActivityLifecycleExtraInfo, ITenantActivityLifecycleResumeData, TENANTACTIVITYLIFECYCLETYPESINFO } from '../../models/pulse365-overview/tenant-activity-lifecycle.model';
import { getChartConfig, TenantActivityStatus, TenantWorkspacesPlatform } from '../../models/tenant-health.model';
import { ITenantExternalSharingExtraInfo, TENANTEXTERNALSHARINGINFO, TenantExternalSharingType } from '../../models/pulse365-overview/tenant-external-sharing.model';
import { ITenantMembershipInsightExtraInfo, TENANTMEMBERSHIPINSIGHTSINFO, TenantMembershipInsightsType } from '../../models/pulse365-overview/tenant-membership-insights.model';
import { ITenantStorageUsedExtraInfo, TenantStorageType, TENANTSTORAGEUSEDTYPESINFO } from '../../models/pulse365-overview/tenant-storage-used.model';
import { ChartBreakDown, ChartWrapData } from 'src/app/modules/automate365-dashboard/models/chart-data.interface';
import { environment } from 'src/environments/environment';
import { WorkspaceService } from 'src/app/services/workspace/workspace.service';
import { DataSecurity, DATASECURITYSENSITIVITYLABELINFO, DataSecuritySensitivityLabelPlatform } from '../../models/pulse365-overview/tenant-data-security.model';
import { IActionResult } from 'src/app/services/http-data/apidata.model';
import { convertMBToReadableUnit, humanizeNumber } from 'src/app/models/util.model';
import { TenantAnalyzerService } from 'src/app/services/tenant-analyzer/tenant-analyzer.service';
import { ITenantAnalyzer } from 'src/app/models/tenant-analzer/tenant-analzer.model';
import { ITenantExternalLinksStatisticsTotals } from '../../models/pulse365-sharinglinks.model';
import { ITenantUserStatisticsTotals } from '../../models/pulse365-user/pulse365-user.model';
import { ITenantWorkspaceStatisticsTotals } from '../../models/tenant-health-workspaces/tenant-health-workspaces.model';
import { WorkspaceLicense } from 'src/app/models/user-licenses.model';
import { ReportId } from '../../models/pulse365-report.model';
import { RelatedContentId } from '../../models/pulse365-related-content-report.model';

@Injectable({
  providedIn: 'root'
})
export class TenantHealthService {
  private api = inject(ApiDataService);
  private workspaceService = inject(WorkspaceService);
  private tenantAnalyzerService = inject(TenantAnalyzerService);

  public tenantId: string | undefined;
  public tenantScanInfo: ITenantAnalyzer;
  public hubAPIUrl = environment.urls.hubApi;

  private tenantHealthSnapshot?: ITenantWorkspacesSnapshot = undefined;
  private tenantHealthExternalSharingSnapshot?: ITenantExternalSharingExtraInfo[] = undefined;
  private tenantHealthMembershipInsightsSnapshot?: ITenantMembershipInsightExtraInfo[] = undefined;
  private tenantHealthStorage?: ITenantStorageUsedExtraInfo = undefined;
  private tenantHealthDataSecurity?: DataSecurity = undefined;
  private tenantHealthLifecycleActivity: { numberDays: number; value: ITenantActivityLifecycleExtraInfo[] }[] = [];
  private hasPulse365Report: boolean | undefined = undefined;

  private pulse365License: WorkspaceLicense | undefined = undefined;

  constructor() {
    this.tenantAnalyzerService.refreshPulse365DashboardInformation$.subscribe(async () => {
      this.tenantHealthSnapshot = undefined;
      this.tenantHealthExternalSharingSnapshot = undefined;
      this.tenantHealthMembershipInsightsSnapshot = undefined;
      this.tenantHealthStorage = undefined;
      this.tenantHealthDataSecurity = undefined;
      this.tenantHealthLifecycleActivity = [];
      this.hasPulse365Report = undefined;
    });

  }

  public setLicenseId(license: WorkspaceLicense) {
    this.pulse365License = license;
  }

  // Validate if the Pulse365 report is available
  // At least should have a UserTenant for Pulse365 App and a scan run completed with origin started on the Pulse365 Dashboard
  public hasValidPulse365ReportGenerated(): boolean {
    this.tenantId = this.workspaceService.getCurrentUserWorkspace()?.info.tenantId;
    this.tenantScanInfo = this.tenantAnalyzerService.getTenantAnalyzerInfo()

    this.hasPulse365Report = this.tenantId !== undefined && this.tenantScanInfo.lastAnalyze?.pulse365 !== undefined && this.tenantScanInfo.lastAnalyze?.pulse365 !== null;
    return this.hasPulse365Report;
  }

  // If it's the first scan being ran for this tenant, we will try to load the base statistics from analyzer
  // Base statistics are just a bunch of simple metrics, analyzed in the beginning of the scan
  public isFirstScanAndIsRunning() {
    const tenantAnalyzerInfo = this.tenantAnalyzerService.getTenantAnalyzerInfo();
    return tenantAnalyzerInfo.lastAnalyze?.pulse365 == null && tenantAnalyzerInfo.isRunning != null && tenantAnalyzerInfo.isRunning !== undefined
  }

  // #region Tenant Workspaces Snapshot

  /**
   * API call to get the tenant workspaces snapshot overview report
   * @returns A promise that resolves to the tenant workspaces snapshot.
   */
  public async getTenantHealthSnapshotOverview(forceRefresh: boolean): Promise<ITenantWorkspacesSnapshot> {
    return new Promise((resolve, reject) => {
      if (this.tenantHealthSnapshot && !forceRefresh) {
        resolve(this.tenantHealthSnapshot);
      }
      else {
        if (!this.hasValidPulse365ReportGenerated() && !this.isFirstScanAndIsRunning()) {
          reject(null)
          return
        }

        this.api.get<ITenantWorkspacesSnapshot>(`${environment.urls.hubApi}api/tenants/${this.tenantId}/health/snapshot?sid=${this.pulse365License?.subscriptionID}`).subscribe((response: IActionResult<ITenantWorkspacesSnapshot>) => {
          if (response.data) {
            this.tenantHealthSnapshot = this.parseSnapshotData(response.data);
            resolve(this.tenantHealthSnapshot);
          } else {
            reject(null);
          }
        }, () => { reject(null); });
      }
    });
  }

  /**
   * Parses the snapshot data to add additional properties and format it for display.
   * @param data The snapshot data to parse.
   * @returns The parsed snapshot data.
   */
  private parseSnapshotData(data: ITenantWorkspacesSnapshot): ITenantWorkspacesSnapshot {

    const platforms: ITenantWorkspacesSnapshotPlatformExtraInfo[] = [];

    // Add all the platforms available in the snapshot on the dataToReturn
    for (const key in TENANTWORKSPACESSNAPSHOTINFO) {
      const typedKey = key as keyof typeof TENANTWORKSPACESSNAPSHOTINFO;

      // Skip hidden platforms
      if (TENANTWORKSPACESSNAPSHOTINFO[typedKey].isHidden) {
        continue;
      }

      const platform = data.platforms.find((platform) => platform.type === typedKey);

      platforms.push({
        type: typedKey,
        total: platform ? platform.total : 0,
        totalConverted: platform ? humanizeNumber(platform.total, 10000) : "0",
        totalTooltip: platform && platform.total > 10000 ? `${platform.total.toLocaleString('en-US', { minimumFractionDigits: 0 })}` : undefined,
        totalLast30Days: platform ? platform.totalLast30Days : 0,
        activeWorkspaces: platform ? platform.activeWorkspaces : 0,
        icon: TENANTWORKSPACESSNAPSHOTINFO[typedKey].icon,
        label: TENANTWORKSPACESSNAPSHOTINFO[typedKey].label,
        help: TENANTWORKSPACESSNAPSHOTINFO[typedKey].help,
        activePercentage: platform && data.total > 0 && platform.activeWorkspaces > -1 ? Math.round((platform.activeWorkspaces / platform.total) * 100) : 0,
        archivePercentage: platform && data.total > 0 && platform.archiveWorkspaces !== undefined && platform.archiveWorkspaces > -1 ? Math.round((platform.archiveWorkspaces / platform.total) * 100) : 0,
      });
    }

    // Order the platforms by order property in ascending order
    platforms.sort((a, b) => { return TENANTWORKSPACESSNAPSHOTINFO[a.type].order - TENANTWORKSPACESSNAPSHOTINFO[b.type].order; });

    return {
      ...data,
      totalConverted: humanizeNumber(data.total, 10000),
      totalTooltip: data.total > 10000 ? `${data.total.toLocaleString('en-US', { minimumFractionDigits: 0 })}` : undefined,
      activePercentage: data.total > 0 ? Math.round((data.active / data.total) * 100) : 0,
      platforms
    };
  }

  //#endregion

  //#region Tenant Workspaces Activity & Lifecycle

  /**
    * API call to get the tenant Activity Lifecycle snapshot overview report
    * @returns A promise that resolves to the tenant workspaces snapshot.
    */
  public async getTenantHealthActivityLifecycle(numberDays: number, forceRefresh: boolean): Promise<ITenantActivityLifecycleExtraInfo[]> {
    return new Promise((resolve, reject) => {

      const index = this.tenantHealthLifecycleActivity.findIndex(item => item.numberDays === numberDays);
      if (index >= 0 && !forceRefresh) {
        resolve(this.tenantHealthLifecycleActivity[index].value);
      }
      else {
        if (!this.hasValidPulse365ReportGenerated() && !this.isFirstScanAndIsRunning()) {
          reject(null)
          return
        }

        this.api.get<ITenantActivityLifecycleExtraInfo[]>(`${environment.urls.hubApi}api/tenants/${this.tenantId}/health/activity?active=${numberDays}&sid=${this.pulse365License?.subscriptionID}`).subscribe((response: IActionResult<ITenantActivityLifecycleExtraInfo[]>) => {
          if (response.data) {
            const data = { numberDays: numberDays, value: this.parseActivityLifecycleData(response.data, numberDays) };
            this.tenantHealthLifecycleActivity.push(data);
            resolve(data.value);
          } else {
            reject(null);
          }
        }, () => { reject(null); });
      }
    });
  }

  /**
  * API call to get the tenant Activity archived workspace card filters totals
  * @returns A promise that resolves to the tenant workspaces snapshot.
  */
  public async getTenantHealthActivityArchivedTotals(): Promise<ITenantActivityArchivedTotals | undefined> {
    return new Promise((resolve) => {
      if (!this.hasValidPulse365ReportGenerated()) {
        resolve(undefined)
        return
      }

      this.api.get<ITenantActivityArchivedTotals>(`${environment.urls.hubApi}api/tenants/${this.tenantId}/health/activity-archived?sid=${this.pulse365License?.subscriptionID}`).subscribe({
        next: (resp) => {
          resolve(resp.data);
        },
        error: () => {
          resolve(undefined)
        }
      })
    })
  }

  /**
  * API call to get the tenant Activity created workspace card filters totals
  * @returns A promise that resolves to the tenant workspaces snapshot.
  */
  public async getTenantHealthActivityCreatedTotals(numberDays: number): Promise<ITenantActivityCreatedTotals | undefined> {
    return new Promise((resolve) => {
      if (!this.hasValidPulse365ReportGenerated()) {
        resolve(undefined)
        return
      }

      this.api.get<ITenantActivityCreatedTotals>(`${environment.urls.hubApi}api/tenants/${this.tenantId}/health/activity-created?active=${numberDays}&sid=${this.pulse365License?.subscriptionID}`).subscribe({
        next: (resp) => {
          resolve(resp.data);
        },
        error: () => {
          resolve(undefined);
        }
      })
    })
  }

  /**
  * API call to get the tenant External Sharing enabled workspaces top cards totals
  * @returns A promise that resolves to the tenant workspaces snapshot.
  */
  public async getTenantHealthExternalSharingCardsTotals(): Promise<ITenantActivityCreatedTotals | undefined> {
    return new Promise((resolve) => {
      if (!this.hasValidPulse365ReportGenerated()) {
        resolve(undefined)
        return
      }

      this.api.get<ITenantActivityCreatedTotals>(`${environment.urls.hubApi}api/tenants/${this.tenantId}/health/external-sharing-statistics?sid=${this.pulse365License?.subscriptionID}`).subscribe({
        next: (resp) => {
          resolve(resp.data);
        },
        error: () => {
          resolve(undefined);
        }
      })
    })
  }

  /**
* API call to get the tenant External Sharing enabled workspaces top cards totals
* @returns A promise that resolves to the tenant workspaces snapshot.
*/
  public async getWorkspaceWithGuestsCardsTotals(): Promise<unknown | undefined> {
    return new Promise((resolve) => {
      if (!this.hasValidPulse365ReportGenerated()) {
        resolve(undefined)
        return
      }

      this.api.get<unknown>(`${environment.urls.hubApi}api/tenants/${this.tenantId}/health/workspace-with-guests-statistics?sid=${this.pulse365License?.subscriptionID}`).subscribe({
        next: (resp) => {
          resolve(resp.data);
        },
        error: () => {
          resolve(undefined);
        }
      })
    })
  }

    /**
* API call to get the tenant Orphaned Workspaces top cards totals
* @returns A promise that resolves to the tenant workspaces snapshot.
*/
  public async getOrphanedWorkspacesCardsTotals(): Promise<unknown | undefined> {
    return new Promise((resolve) => {
      if (!this.hasValidPulse365ReportGenerated()) {
        resolve(undefined)
        return
      }

      this.api.get<unknown>(`${environment.urls.hubApi}api/tenants/${this.tenantId}/health/orphaned-statistics?sid=${this.pulse365License?.subscriptionID}`).subscribe({
        next: (resp) => {
          resolve(resp.data);
        },
        error: () => {
          resolve(undefined);
        }
      })
    })
  }

  /**
* API call to get the tenant workspace card totals
* @returns A promise that resolves to the tenant workspaces snapshot.
*/
  public async getTenantHealthWorkspaceCardTotals(platform: TenantWorkspacesPlatform | "all"): Promise<ITenantWorkspaceStatisticsTotals | undefined> {
    return new Promise((resolve) => {
      if (!this.hasValidPulse365ReportGenerated()) {
        resolve(undefined)
        return
      }

      this.api.get<ITenantWorkspaceStatisticsTotals>(`${environment.urls.hubApi}api/tenants/${this.tenantId}/health/workspaces-statistics/${platform}?sid=${this.pulse365License?.subscriptionID}`).subscribe({
        next: (resp) => {
          resolve(resp.data);
        },
        error: () => {
          resolve(undefined);
        }
      })
    })
  }

  /**
  * API call to get the sensitivity label not applied workspace card totals
  * @returns A promise that resolves to the tenant workspaces snapshot.
  */
  public async getSensitivityLabelNotAppliedCardTotals(): Promise<ITenantWorkspaceStatisticsTotals | undefined> {
    return new Promise((resolve) => {
      if (!this.hasValidPulse365ReportGenerated()) {
        resolve(undefined)
        return
      }

      this.api.get<ITenantWorkspaceStatisticsTotals>(`${environment.urls.hubApi}api/tenants/${this.tenantId}/health/security-statistics?sid=${this.pulse365License?.subscriptionID}`).subscribe({
        next: (resp) => {
          resolve(resp.data);
        },
        error: () => {
          resolve(undefined);
        }
      })
    })
  }

  /**
* API call to get the tenant guests card totals
* @returns A promise that resolves to the tenant guests snapshot.
*/
  public async getTenantHealthGuestsCardTotals(): Promise<ITenantUserStatisticsTotals | undefined> {
    return new Promise((resolve) => {
      if (!this.hasValidPulse365ReportGenerated()) {
        resolve(undefined)
        return
      }

      this.api.get<ITenantUserStatisticsTotals>(`${environment.urls.hubApi}api/tenants/${this.tenantId}/health/guests-statistics?sid=${this.pulse365License?.subscriptionID}`).subscribe({
        next: (resp) => {
          resolve(resp.data);
        },
        error: () => {
          resolve(undefined);
        }
      })
    })
  }

  /**
* API call to get the external links statistics
* @returns A promise that resolves to the external links statistics.
*/
  public async getTenantHealthExternalLinksCardTotals(): Promise<ITenantExternalLinksStatisticsTotals | undefined> {
    return new Promise((resolve) => {
      if (!this.hasValidPulse365ReportGenerated()) {
        resolve(undefined)
        return
      }

      this.api.get<ITenantExternalLinksStatisticsTotals>(`${environment.urls.hubApi}api/tenants/${this.tenantId}/health/externalLinks-statistics?sid=${this.pulse365License?.subscriptionID}`).subscribe({
        next: (resp) => {
          resolve(resp.data);
        },
        error: () => {
          resolve(undefined);
        }
      })
    })
  }

  /**
  * Parses the snapshot data to add additional properties and format it for display.
  * @param data The snapshot data to parse.
  * @param numberDays The number of days.
  * @returns The parsed snapshot data.
  */
  private parseActivityLifecycleData(data: ITenantActivityLifecycleExtraInfo[], numberDays: number): ITenantActivityLifecycleExtraInfo[] {

    const dataToReturn: ITenantActivityLifecycleExtraInfo[] = [];

    // Add all the platforms available in the snapshot on the dataToReturn
    for (const key in TENANTACTIVITYLIFECYCLETYPESINFO) {
      const typedKey = key as keyof typeof TENANTACTIVITYLIFECYCLETYPESINFO;

      const activity = data.find((activity) => activity.type === typedKey);
      if (!activity) {
        dataToReturn.push({ type: typedKey, total: 0, resumeData: [{ date: new Date(new Date().setDate(-numberDays)), value: 0 }, { date: new Date(), value: 0 }] });
      }
      else {
        dataToReturn.push(activity);
      }
    }

    dataToReturn.forEach((activity) => {
      activity.icon = TENANTACTIVITYLIFECYCLETYPESINFO[activity.type]?.icon;
      activity.label = TENANTACTIVITYLIFECYCLETYPESINFO[activity.type]?.label;
      activity.color = TENANTACTIVITYLIFECYCLETYPESINFO[activity.type]?.color;
      activity.iconColor = TENANTACTIVITYLIFECYCLETYPESINFO[activity.type]?.iconColor;
      activity.stats = activity.color ? getChartConfig(activity.color.red, activity.color.green, activity.color.blue, "Workspaces") : undefined;
      const helpText = TENANTACTIVITYLIFECYCLETYPESINFO[activity.type]?.help;
      if (helpText) { activity.help = { text: helpText.text, theme: 'light', position: 'top', autoClose: true }; }

      if (activity.stats) {
        activity.stats.data.datasets[0].data = activity.resumeData.map((item: ITenantActivityLifecycleResumeData) => item.value);
        activity.stats.data.labels = activity.resumeData.map((item: ITenantActivityLifecycleResumeData) => new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: '2-digit' }));
      }

      activity.action = `../workspaces/activity/${activity.type}`
      activity.totalConverted = humanizeNumber(activity.total, 1000)
      activity.totalTooltip = activity.total > 1000 ? `${activity.total.toLocaleString('en-US', { minimumFractionDigits: 0 })}` : undefined
    });

    // Order the platforms by order property in ascending order
    dataToReturn.sort((a, b) => { return TENANTACTIVITYLIFECYCLETYPESINFO[a.type]?.order - TENANTACTIVITYLIFECYCLETYPESINFO[b.type]?.order; });

    return dataToReturn;
  }

  //#endregion

  //#region Tenant External Sharing

  /**
    * API call to get the tenant Activity Lifecycle snapshot overview report
    * @returns A promise that resolves to the tenant workspaces snapshot.
    */
  public async getExternalSharingSnapshot(forceRefresh: boolean): Promise<ITenantExternalSharingExtraInfo[]> {
    return new Promise((resolve, reject) => {
      if (this.tenantHealthExternalSharingSnapshot && !forceRefresh) {
        resolve(this.tenantHealthExternalSharingSnapshot);
      }
      else {
        if (!this.hasValidPulse365ReportGenerated() && !this.isFirstScanAndIsRunning()) {
          reject(null)
          return
        }

        this.api.get<ITenantExternalSharingExtraInfo[]>(`${environment.urls.hubApi}api/tenants/${this.tenantId}/health/externalSharing?sid=${this.pulse365License?.subscriptionID}`).subscribe((response: IActionResult<ITenantExternalSharingExtraInfo[]>) => {
          if (response.data) {
            this.tenantHealthExternalSharingSnapshot = this.parseExternalSharingSnapshotData(response.data);
            resolve(this.tenantHealthExternalSharingSnapshot);
          } else {
            reject(null);
          }
        }, () => { reject(null); });
      }
    });
  }

  /**
  * Parses the snapshot data to add additional properties and format it for display.
  * @param data The snapshot data to parse.
  * @returns The parsed snapshot data.
  */
  private parseExternalSharingSnapshotData(data: ITenantExternalSharingExtraInfo[]): ITenantExternalSharingExtraInfo[] {

    data.forEach((externalSharing) => {
      externalSharing.icon = TENANTEXTERNALSHARINGINFO[externalSharing.type].icon;
      externalSharing.label = TENANTEXTERNALSHARINGINFO[externalSharing.type].label;
      externalSharing.iconColor = TENANTEXTERNALSHARINGINFO[externalSharing.type].iconColor;
      externalSharing.route = TENANTEXTERNALSHARINGINFO[externalSharing.type].route;
      externalSharing.help = TENANTEXTERNALSHARINGINFO[externalSharing.type].help;

      externalSharing.totalConverted = humanizeNumber(externalSharing.total, 1000)
      externalSharing.totalTooltip = externalSharing.total > 1000 ? `${externalSharing.total.toLocaleString('en-US', { minimumFractionDigits: 0 })}` : undefined

      if (externalSharing.percentage) { externalSharing.percentage = Number(externalSharing.percentage.toFixed(2)); }

      if (externalSharing.type === TenantExternalSharingType.Guests || externalSharing.type === TenantExternalSharingType.ExternalUsers) {
        externalSharing.activePercentageLabel = "active";
      }
      else if (externalSharing.type === TenantExternalSharingType.ExternalSharingLinks || externalSharing.type === TenantExternalSharingType.SitesWithExternalSharing) {
        externalSharing.activePercentageLabel = "shared";
      }
      else if (externalSharing.type === TenantExternalSharingType.WorkspacesWithGuests) {
        externalSharing.activePercentageLabel = "with guests";
      }
      else {
        externalSharing.activePercentageLabel = "";
      }

    });

    // Order the platforms by order property in ascending order
    data.sort((a, b) => { return TENANTEXTERNALSHARINGINFO[a.type].order - TENANTEXTERNALSHARINGINFO[b.type].order; });

    return data;
  }

  //#endregion

  //#region Tenant Membership Insights

  /**
    * API call to get the tenant Membership Insights snapshot overview report
    * @returns A promise that resolves to the tenant workspaces snapshot.
    */
  public async getMembershipInsightsSnapshot(forceRefresh: boolean): Promise<ITenantMembershipInsightExtraInfo[]> {
    return new Promise((resolve, reject) => {
      if (this.tenantHealthMembershipInsightsSnapshot && !forceRefresh) {
        resolve(this.tenantHealthMembershipInsightsSnapshot);
      }
      else {
        if (!this.hasValidPulse365ReportGenerated()) {
          reject(null)
          return
        }

        this.api.get<ITenantMembershipInsightExtraInfo[]>(`${environment.urls.hubApi}api/tenants/${this.tenantId}/health/membership?sid=${this.pulse365License?.subscriptionID}`).subscribe((response: IActionResult<ITenantMembershipInsightExtraInfo[]>) => {
          this.tenantHealthMembershipInsightsSnapshot = this.parseMembershipInsightsSnapshotData(response.data);
          resolve(this.tenantHealthMembershipInsightsSnapshot);
        }, () => { reject(null); });
      }
    });
  }

  public getCurrentMembershipInsightsSnapshot(): ITenantMembershipInsightExtraInfo[] | undefined {
    return this.tenantHealthMembershipInsightsSnapshot;
  }

  /**
  * Parses the snapshot data to add additional properties and format it for display.
  * @param data The snapshot data to parse.
  * @returns The parsed snapshot data.
  */
  private parseMembershipInsightsSnapshotData(data: ITenantMembershipInsightExtraInfo[]): ITenantMembershipInsightExtraInfo[] {

    data.forEach((activity) => {
      activity.icon = TENANTMEMBERSHIPINSIGHTSINFO[activity.type].icon;
      activity.label = TENANTMEMBERSHIPINSIGHTSINFO[activity.type].label;
      activity.iconColor = TENANTMEMBERSHIPINSIGHTSINFO[activity.type].iconColor;
      activity.route = TENANTMEMBERSHIPINSIGHTSINFO[activity.type].route;
      activity.help = TENANTMEMBERSHIPINSIGHTSINFO[activity.type].help;
      activity.totalConverted = humanizeNumber(activity.total, 1000);
      activity.totalTooltip = activity.total > 1000 ? `${activity.total.toLocaleString('en-US', { minimumFractionDigits: 0 })}` : undefined;
      activity.archivePercentage = activity.archived ? Number(((activity.archived / activity.total) * 100).toFixed(1)) : undefined;
    });

    // Order the platforms by order property in ascending order
    data.sort((a, b) => { return TENANTMEMBERSHIPINSIGHTSINFO[a.type].order - TENANTMEMBERSHIPINSIGHTSINFO[b.type].order; });

    return data;
  }
  //#endregion

  //#region Tenant Storage used

  /**
    * API call to get the tenant Activity Lifecycle snapshot overview report
    * @returns A promise that resolves to the tenant workspaces snapshot.
    */
  public async getTenantHealthStorageUsed(forceRefresh: boolean): Promise<ITenantStorageUsedExtraInfo> {
    return new Promise((resolve, reject) => {
      if (this.tenantHealthStorage && !forceRefresh) {
        resolve(this.tenantHealthStorage);
      }
      else {
        if (!this.hasValidPulse365ReportGenerated() && !this.isFirstScanAndIsRunning()) {
          reject(null)
          return
        }

        this.api.get<ITenantStorageUsedExtraInfo>(`${environment.urls.hubApi}api/tenants/${this.tenantId}/health/storage?sid=${this.pulse365License?.subscriptionID}`).subscribe((response: IActionResult<ITenantStorageUsedExtraInfo>) => {
          if (response.data) {
            this.tenantHealthStorage = this.parseStorageUsedData(response.data);
            resolve(this.tenantHealthStorage);
          } else {
            reject(null);
          }
        }, () => { reject(null); });
      }
    });
  }

  /**
 * Parses the snapshot data to add additional properties and format it for display.
 * @param data The snapshot data to parse.
 * @returns The parsed snapshot data.
 */
  private parseStorageUsedData(data: ITenantStorageUsedExtraInfo): ITenantStorageUsedExtraInfo {
    // Convert the total value
    data.totalConverted = data.total != null && data.total >= 0 ? convertMBToReadableUnit(data.total) : "";
    data.usedConverted = data.used != null && data.used >= 0 ? convertMBToReadableUnit(data.used) : "";
    data.percentageUsed = data.total != null && data.used != null && data.total > 0 ? +(Math.round((data.used / data.total) * 100)) : 0;

    data.platforms.forEach(platform => {
      platform.totalConverted = platform.total != null && platform.total >= 0 ? convertMBToReadableUnit(platform.total) : "";
      platform.usedConverted = platform.used != null && platform.used >= 0 ? convertMBToReadableUnit(platform.used) : "";
      const leftConverted = platform.free != null ? convertMBToReadableUnit(platform.free) : "0";

      // Split into number and unit
      const [valueStr, unit] = leftConverted.split(' ');

      // Convert numeric part to number, default to 0 if invalid
      platform.leftConverted = isNaN(Number(valueStr)) ? 0 : Number(valueStr);

      // Store unit separately
      platform.leftConvertedUnit = unit ?? "";
      platform.percentageUsed = platform.total != null && platform.total >= 0 && platform.used != null && platform.used > 0 ? Math.round((platform.used / platform.total) * 100) : 0;
    })

    const platformIndex = data.platforms.findIndex(x => x.type === TenantStorageType.SharePoint);
    if (platformIndex >= 0) {
      data.platforms[platformIndex].siteTypes = data.platforms[platformIndex].siteTypes?.sort((a, b) => (b.used ?? 0) - (a.used ?? 0)) ?? [];
      data.platforms[platformIndex].siteTypes?.forEach((platform) => {
        platform.label = TENANTSTORAGEUSEDTYPESINFO[platform.type].label;
        platform.color = TENANTSTORAGEUSEDTYPESINFO[platform.type].color;
        platform.totalConverted = platform.total != null && platform.total >= 0 ? convertMBToReadableUnit(platform.total) : "";
        platform.usedConverted = platform.used != null && platform.used >= 0 ? convertMBToReadableUnit(platform.used) : "";
        platform.percentageUsed = data.total != null && data.total >= 0 && platform.used != null && platform.used > 0 ? Math.round((platform.used / data.total) * 100) : 0;
      });
    }

    return data;
  }

  public getStorageUsedChart(data: ITenantStorageUsedExtraInfo): ChartWrapData {
    const breakdown: ChartBreakDown[] = [];
    const values: number[] = [];
    const colors: string[] = [];
    const labels: string[] = [];
    const platform = data.platforms.find(x => x.type === TenantStorageType.SharePoint);
    const siteTypes = platform?.siteTypes ?? [];

    // This is to know if it's the first scan for this tenant
    // If it is, we just create the doghnut chart empty
    const isEmpty = siteTypes.find(s => s.total != null) === undefined;

    siteTypes.forEach((siteType, index) => {
      let numberPercentage = 0;
      if (platform && platform.used && platform.used > 0) {
        numberPercentage = siteType.used ? (siteType.used / platform.used) * 100 : 0;
      }
      values.push(numberPercentage);
      colors.push(siteType.color ?? "white");
      labels.push(siteType.label ?? "-");

      breakdown.push({
        order: index,
        title: siteType.label ?? "-",
        total: siteType.used ?? 0,
        color: siteType.color,
        totalConverted: siteType.used ? convertMBToReadableUnit(siteType.used) : "",
        percentage: `${Math.round(numberPercentage * Math.pow(10, 0)) / Math.pow(10, 0)}`
      })
    });

    if (isEmpty) {
      colors.push("#263036");
      values.push(100);
    }

    const chartData = {
      labels: labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
          //circumference: platform?.used ?? 360,
          cutout: '65%',
          borderWidth: 0,
          borderRadius: 0,
          hoverBackgroundColor: colors,
          hoverBorderColor: colors
        },
        // {
        //   data: [platform ? platform.used : 100],
        //   backgroundColor: ['#282E32'],
        //   cutout: '88%',
        //   borderWidth: 0
        // }
      ]
    }
    return {
      data: chartData,
      hasSubscription: true,
      breakDown: breakdown,
      options: {
        plugins: {
          tooltip: {
            enabled: !isEmpty
          }
        }
      }
    }
  }

  //#endregion

  //#region Tenant Storage used

  /**
    * API call to get the tenant Activity Lifecycle snapshot overview report
    * @returns A promise that resolves to the tenant workspaces snapshot.
    */
  public async getTenantHealthDataSecurity(forceRefresh: boolean): Promise<DataSecurity> {
    return new Promise((resolve, reject) => {

      if (this.tenantHealthDataSecurity && !forceRefresh) {
        resolve(this.tenantHealthDataSecurity);
      }
      else {
        if (!this.hasValidPulse365ReportGenerated()) {
          reject(null)
          return
        }

        this.api.get<DataSecurity>(`${environment.urls.hubApi}api/tenants/${this.tenantId}/health/security?sid=${this.pulse365License?.subscriptionID}`).subscribe((response: IActionResult<DataSecurity>) => {
          this.tenantHealthDataSecurity = this.parseDataSecurityData(response.data);
          resolve(this.tenantHealthDataSecurity);
        }, () => { reject(null); });
      }

    });
  }

  /**
 * Parses the snapshot data to add additional properties and format it for display.
 * @param data The snapshot data to parse.
 * @returns The parsed snapshot data.
 */
  private parseDataSecurityData(data: DataSecurity): DataSecurity {

    for (const key in DATASECURITYSENSITIVITYLABELINFO) {
      const typedKey = key as keyof typeof data as DataSecuritySensitivityLabelPlatform;

      const index = data.sensitivityLabel.notAppliedByPlatform.findIndex((platform) => platform.platform === typedKey);

      if (index > -1) {
        data.sensitivityLabel.notAppliedByPlatform[index].order = DATASECURITYSENSITIVITYLABELINFO[typedKey].order;
        data.sensitivityLabel.notAppliedByPlatform[index].label = DATASECURITYSENSITIVITYLABELINFO[typedKey].label;
        data.sensitivityLabel.notAppliedByPlatform[index].icon = DATASECURITYSENSITIVITYLABELINFO[typedKey].icon;
        data.sensitivityLabel.notAppliedByPlatform[index].countConverted = humanizeNumber(data.sensitivityLabel.notAppliedByPlatform[index].count, 1000);
        data.sensitivityLabel.notAppliedByPlatform[index].countTooltip = data.sensitivityLabel.notAppliedByPlatform[index].count > 1000 ? `${data.sensitivityLabel.notAppliedByPlatform[index].count.toLocaleString('en-US', { minimumFractionDigits: 0 })}` : undefined
      }
      else {
        data.sensitivityLabel.notAppliedByPlatform.push({
          platform: typedKey,
          count: 0,
          countConverted: "0",
          label: DATASECURITYSENSITIVITYLABELINFO[typedKey].label,
          icon: DATASECURITYSENSITIVITYLABELINFO[typedKey].icon,
          order: DATASECURITYSENSITIVITYLABELINFO[typedKey].order
        });
      }
    }

    data.sensitivityLabel.notAppliedByPlatform = data.sensitivityLabel.notAppliedByPlatform?.sort((a, b) => a.order - b.order) ?? [];
    data.retentionLabels?.forEach(label => {
      label.usageCountConverted = humanizeNumber(label.usageCount, 1000);
    });
    data.retentionLabels = data.retentionLabels?.sort((a, b) => b.usageCount - a.usageCount) ?? [];

    return data;
  }

  //#endregion

  // Function to get top cards totals (Only needed if report has top cards)
  public readonly getReportTopCardsTotalsAPICall: { [key in ReportId]: (() => Promise<unknown>) | undefined } = {
    ["all"]: () => this.getTenantHealthWorkspaceCardTotals("all"),
    [TenantWorkspacesPlatform.SharePoint]: () => this.getTenantHealthWorkspaceCardTotals(TenantWorkspacesPlatform.SharePoint),
    [TenantWorkspacesPlatform.Teams]: () => this.getTenantHealthWorkspaceCardTotals(TenantWorkspacesPlatform.Teams),
    [TenantWorkspacesPlatform.Groups]: () => this.getTenantHealthWorkspaceCardTotals(TenantWorkspacesPlatform.Groups),
    [TenantWorkspacesPlatform.Engage]: () => this.getTenantHealthWorkspaceCardTotals(TenantWorkspacesPlatform.Engage),
    [TenantWorkspacesPlatform.PowerBI]: () => this.getTenantHealthWorkspaceCardTotals(TenantWorkspacesPlatform.PowerBI),
    [TenantActivityStatus.Active]: undefined,
    [TenantActivityStatus.Inactive]: undefined,
    [TenantActivityStatus.Created]: undefined,
    [TenantActivityStatus.Archived]: () => this.getTenantHealthActivityArchivedTotals(),
    [TenantActivityStatus.Deleted]: undefined,
    [TenantActivityStatus.Renewed]: undefined,
    [TenantExternalSharingType.ExternalSharingLinks]: () => this.getTenantHealthExternalLinksCardTotals(),
    [TenantExternalSharingType.WorkspacesWithGuests]: () => this.getWorkspaceWithGuestsCardsTotals(),
    [TenantExternalSharingType.ExternalUsers]: undefined,
    [TenantExternalSharingType.Guests]: () => this.getTenantHealthGuestsCardTotals(),
    [TenantExternalSharingType.SitesWithExternalSharing]: () => this.getTenantHealthExternalSharingCardsTotals(),
    [TenantMembershipInsightsType.InactiveMemberships]: undefined,
    [TenantMembershipInsightsType.OrphanedWorkspaces]: () => this.getOrphanedWorkspacesCardsTotals(),
    [TenantMembershipInsightsType.PendingMemberships]: undefined,
    [TenantMembershipInsightsType.WorkspacesWithShadowUsers]: undefined,
    ["sensitivityLabelNotApplied"]: () => this.getSensitivityLabelNotAppliedCardTotals(),
    ["storage-sharepoint"]: () => this.getTenantHealthWorkspaceCardTotals("all"),
    ["storage-onedrive"]: undefined,
    ["sharepoint-alerts"]: undefined,
    ["sharepoint-pages"]: undefined
  }

  // Function to get top cards totals (Only needed if report has top cards)
  public readonly getRelatedContentReportTopCardsTotalsAPICall: { [key in RelatedContentId]: (() => Promise<unknown>) | undefined } = {
    ["site-owners"]: undefined,
    ["site-members"]: undefined,
    ["channel-sites"]: undefined,
    ["powerbi-reports"]: undefined,
    ["powerbi-dashboards"]: undefined,
    ["powerbi-datasets"]: undefined,
    ["site-admins"]: undefined,
    ["site-visitors"]: undefined,
    ["guests"]: undefined,
    ["workspace-guests"]: undefined,
    ["external-users"]: undefined,
    ["owners"]: undefined,
    ["members"]: undefined,
    ["powerbi-admins"]: undefined,
    ["powerbi-contributors"]: undefined,
    ["powerbi-viewers"]: undefined,
    ["shadow-users"]: undefined,
    ["anonymous-links"]: undefined,
    ["groups"]: undefined,
    ["shared-assets"]: undefined,
    ["shared-with"]: undefined,
    ["site-pending-membership"]: undefined,
    ["site-pages"]: undefined,
    ["site-classic-pages"]: undefined,
    ["site-modern-pages"]: undefined,
  }
}

